import React from 'react';
import { useNavigate, navigate } from '@reach/router';
import moment from 'moment-timezone';
import { Formik } from 'formik';
import { useToasts } from 'freemium-ui';
import useBeforeUnload from 'shared/hooks/useBeforeUnload';
import { getValidationSchema, createFormTreeFromConfig } from 'helpers/forms';
import request from 'helpers/request';
import { getTimeZoneFromAccounts } from 'helpers/date';
import { queryCache } from 'react-query';
import { useTranslation } from 'react-i18next';
import { StyledForm } from './style';
import ButtonWithMenu from '../ButtonWithMenu';
import { useIncidentSource } from '../../screens/incidents/hooks';
import {
  PRIVATE_ID,
  getFieldsConfig,
  getValuesFromTemplate,
  getDefaultIncident,
  PRIVACY_OPTIONS
} from './constant';
import FormFooter from './components/FormFooter';

// @todo: temp typecasting refactor out later
function convertAllIdToString(components) {
  return components.map(c => ({ ...c, component: c.component.toString() }));
}
function convertTimeZoneTimeToUTC(time, tz) {
  return moment
    .tz(time, tz || 'UTC')
    .utc()
    .format();
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

type IncidentFormType = {
  edit?: string,
  id?: string,
  getIncidentFromStore?: any,
  updateIncidentInStore?: any,
  close?: any,
  currentAccountMeta?: any,
  alert?: any,
  initialValues?: any,
  currentAccount: any,
  incidentType: any,
  updateLocalFormPrivacy: Function,
  formPrivacy?: { id: string }
};
const IncidentForm = (props: IncidentFormType) => {
  const [, setToast] = useToasts();
  const { t } = useTranslation();
  const {
    incidentType,
    close,
    alert,
    currentAccount,
    currentAccountMeta,
    initialValues,
    updateLocalFormPrivacy,
    formPrivacy
  } = props;

  const currentIncident = props.edit
    ? props.getIncidentFromStore(props.id)
    : undefined;
  const timezone = getTimeZoneFromAccounts(currentAccount);

  const [templateValues, settemplateValues] = React.useState(
    getValuesFromTemplate(initialValues)
  );
  const [defaultIncidentValue, setDefaultIncidentValue] = React.useState(
    getDefaultIncident(timezone, currentIncident, incidentType, alert)
  );

  const createIncident = async (values, setErrors, formik) => {
    const hasEndTime =
      incidentType === 'planned' ||
      values.backfill ||
      (currentIncident && currentIncident.status !== 'OP');
    const isNotClosedSMUpdate =
      !currentIncident || (currentIncident && currentIncident.status !== 'CL');
    const startTime = convertTimeZoneTimeToUTC(values.start, timezone);
    const endTime = convertTimeZoneTimeToUTC(values.end, timezone);
    const payload = {
      title: values.name,
      description: values.description,
      start_time: startTime,
      ...(hasEndTime && { end_time: endTime }),
      is_backfill: values.backfill,
      bulk_component_update_id: initialValues.bulk_component_update_id,
      ...(incidentType === 'planned' &&
        isNotClosedSMUpdate && { scheduled_start_time: startTime }),
      ...(incidentType === 'planned' &&
        isNotClosedSMUpdate && { scheduled_end_time: endTime }),
      is_private: values.privacy.id === 'private',
      is_planned: incidentType === 'planned',
      source: values.source ? values.source.id : null,
      override_affected_component_status_history: currentIncident
        ? !currentIncident.is_private && currentIncident.status !== 'PL'
        : values.privacy.id === 'private'
        ? false
        : incidentType === 'public',
      ...(incidentType === 'planned' && {
        is_auto_update_status_on_scheduled_start: String(
          values.is_auto_update_status_on_start
        )
      }),
      ...(incidentType === 'planned' && {
        is_auto_update_status_on_scheduled_end: String(
          values.is_auto_update_status_on_end
        )
      }),
      ...(incidentType === 'planned' && {
        is_auto_update_component_status_on_scheduled_start: String(
          values.is_auto_update_status_on_start
        )
      }),
      ...(incidentType === 'planned' && {
        is_auto_update_component_status_on_scheduled_end: String(
          values.is_auto_update_status_on_end
        )
      }),
      notification_options: {
        email_before_one_hour: values.email_before_one_hour ? 'true' : 'false', // String(values.email_before_one_hour),
        email_before_day_hour: values.email_before_day_hour ? 'true' : 'false', // String(values.email_before_day_hour),
        email_on_start: values.email_on_start ? 'true' : 'false', // String(values.email_on_start),
        email_on_complete: values.email_on_complete ? 'true' : 'false', // String(values.email_on_complete),
        send_tweet: values.send_tweet ? 'true' : 'false', // String(values.send_tweet),
        send_email: values.send_email ? 'true' : 'false' // String(values.send_email)
      }
    };
    // if (!currentIncident) {
    // @todo:affected-edit send this field if only there are changes
    payload.affected_components = convertAllIdToString(
      values.affected || values.affected_components || []
    );

    const response = await request(
      `/incidents/${currentIncident ? props.id : ''}`,
      {
        method: `${currentIncident ? 'PATCH' : 'POST'}`,
        payload
      }
    );
    if (response) {
      if (response.response && response.response.non_field_errors) {
        setErrors({
          start: response.response.non_field_errors[0]
        });
        formik.setSubmitting(false);
        return;
      }
      if (currentIncident) {
        const incidentTypeTextKey =   incidentType === 'planned'
                ? 'components.createIncident.maintenance'
                : 'components.createIncident.incident';
        const incidentTypeText = capitalize(t(incidentTypeTextKey));
        setToast({
          type: 'success',
          text: incidentTypeText,
          delay: 3000
        });
        props.updateIncidentInStore(response);
        queryCache.setQueryData(
          [{ incident: String(response.id) }],
          () => response
        );
        setTimeout(() => {
          navigate(`/admin/incidents/${incidentType}/${response.id}`);
        }, 0);
      } else {
        const incidentTypeText =   incidentType === 'planned'
                ? (t('components.createIncident.maintenanceScheduled'))
                : (t('components.createIncident.incidentCreated'));
        setToast({
          type: 'success',
          text: incidentTypeText,
          delay: 3000
        });
        setTimeout(() => {
          navigate(`/admin/incidents/${incidentType}/${response.id}`);
        }, 0);
      }
    }
  };

  const { data: incidentSources } = useIncidentSource();
  // const isCancelledSM = currentIncident && currentIncident.status === 'CN';
  const isPrivate = incidentType === PRIVATE_ID;

  React.useEffect(() => {
    const dIncidentValue = getDefaultIncident(
      timezone,
      currentIncident,
      incidentType,
      alert
    );
    const tValues = getValuesFromTemplate(initialValues);
    setDefaultIncidentValue(dIncidentValue);
    settemplateValues(tValues);
  }, [incidentType, alert, initialValues, currentAccount, currentIncident]);

  return (
    <Formik
      initialValues={{ ...defaultIncidentValue, ...templateValues }}
      onSubmit={(values, actions) => {
        createIncident(values, actions.setErrors, actions);
      }}
      enableReinitialize
      validateOnBlur={false}
      validationSchema={() =>
        getValidationSchema(incidentType, timezone, currentIncident)
      }
    >
      {formProp => {
        useBeforeUnload(
          formProp.dirty,
          t('components.createIncident.changesMayNotBeSaved')
        );
        if (formPrivacy && formPrivacy.id !== formProp.values.privacy.id) {
          updateLocalFormPrivacy(
            PRIVACY_OPTIONS.find(p => p.id === formProp.values.privacy.id)
          );
        }
        return (
          <StyledForm isPrivate={isPrivate} onSubmit={formProp.handleSubmit}>
            {createFormTreeFromConfig(
              getFieldsConfig(
                incidentType,
                formProp.values.backfill,
                formProp.values.privacy,
                timezone,
                currentIncident,
                incidentSources
              ),
              props,
              formProp.values
            )}
            <FormFooter
              type={incidentType}
              currentIncident={currentIncident}
              close={close}
              activeAccount={currentAccount}
              currentAccountMeta={currentAccountMeta}
              values={formProp.values}
              incidentType={incidentType}
              submitForm={formProp.submitForm}
              formProp={formProp}
            />
          </StyledForm>
        );
      }}
    </Formik>
  );
};

const CreateIncident = () => {
  const useNavigateFunc = useNavigate();
  const open = (incident, backfill = false) => {
    useNavigateFunc(
      `/admin/incidents/create?type=${incident.id}&backfill=${backfill}`
    );
  };
  return <ButtonWithMenu createIncident={open} />;
};

export { IncidentForm };
export default CreateIncident;
